import React, { useState } from "react";
import { toast } from "sonner";

import { useInterfacePlaces, usePatchInterface } from "@/shared/api/interfaces";
import { GhostButton, PrimaryButton } from "@/shared/components/settings-atoms";
import { catalogFor } from "@/shared/lib/interface-catalog";
import { FormError } from "./FormError";
import { InterfaceDialog } from "./InterfaceDialog";

import type { Interface } from "@/shared/types";

/** The most channels the service accepts for one bot. */
const MAX_CHANNELS = 25;

/**
 * Pick the channels where a Teams bot answers every message; elsewhere it
 * answers only when mentioned. Channels come from `GET …/places`, grouped by
 * team (the teams the app was added to). A saved channel the bot cannot see
 * right now (a team it was removed from, Microsoft down) is kept and counted,
 * and listed in a note where it can be removed, so a save never wipes
 * configuration the list failed to show.
 */
export function TeamsBotChannels({ iface, onClose }: { iface: Interface; onClose: () => void }) {
  const places = useInterfacePlaces(iface.id);
  const patch = usePatchInterface();
  const [picked, setPicked] = useState<string[]>(iface.answer_all_in ?? []);
  const meta = catalogFor(iface.type)!;

  const visible = new Set((places.data ?? []).flatMap((place) => place.channels.map((c) => c.id)));
  const picks = picked;
  const unseen = places.data ? picked.filter((id) => !visible.has(id)) : [];
  const full = picks.length >= MAX_CHANNELS;

  const toggle = (id: string) =>
    setPicked(picks.includes(id) ? picks.filter((p) => p !== id) : [...picks, id]);

  const save = () =>
    patch.mutate(
      { id: iface.id, body: { answer_all_in: picks } },
      {
        onSuccess: () => {
          toast.success("Bot channels saved");
          onClose();
        },
      },
    );

  return (
    <InterfaceDialog
      meta={meta}
      title="Bot channels"
      description="The agent answers every message in these channels. Elsewhere it answers only when mentioned."
      onClose={onClose}
      footer={
        <>
          <span className="mr-auto font-mono text-[12px] tabular-nums text-[var(--text-tertiary)]">
            {picks.length} of {MAX_CHANNELS}
          </span>
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton type="button" onClick={save} disabled={!places.data || patch.isPending}>
            {patch.isPending ? "Saving…" : "Save"}
          </PrimaryButton>
        </>
      }
    >
      {places.isPending && <p className="m-0 text-[12.5px] text-[var(--text-quaternary)]">Loading channels…</p>}
      {places.error && <FormError error={places.error} words={{ 3110003: "Couldn't reach Microsoft. Try again." }} />}
      {places.data?.length === 0 && (
        <p className="m-0 text-[12.5px] leading-[1.5] text-[var(--text-tertiary)]">
          The app isn't in a team yet. Add it to a team in Teams, then come back.
        </p>
      )}
      {places.data?.map((place) => (
        <fieldset key={place.guild_id} className="m-0 min-w-0 border-0 p-0">
          <legend className="mb-1 p-0 text-[12.5px] font-medium text-[var(--text-secondary)]">{place.guild}</legend>
          <div className="space-y-0.5">
            {place.channels.map((channel) => {
              const on = picks.includes(channel.id);
              return (
                <label
                  key={channel.id}
                  className="flex cursor-pointer items-center gap-2.5 rounded-[8px] px-2 py-1 text-[13px] text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
                >
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={!on && full}
                    onChange={() => toggle(channel.id)}
                    className="shrink-0 accent-[var(--text-primary)]"
                  />
                  <span className="min-w-0 truncate"># {channel.name}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
      {unseen.length > 0 && (
        <p className="m-0 flex items-center gap-2 text-[12.5px] leading-[1.5] text-[var(--text-tertiary)]">
          <span className="min-w-0 flex-1">
            {unseen.length} saved {unseen.length === 1 ? "channel" : "channels"} the bot can't see right now.
          </span>
          <GhostButton type="button" onClick={() => setPicked(picked.filter((id) => visible.has(id)))}>
            Remove
          </GhostButton>
        </p>
      )}
      {full && <p className="m-0 text-[12.5px] text-[var(--text-tertiary)]">That's the most one bot can answer in full: {MAX_CHANNELS}.</p>}
    </InterfaceDialog>
  );
}
